import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Typography
} from '@mui/material';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import SelectField from 'src/common/formFields/SelectField';
import DatePickerField from 'src/common/formFields/DatePickerField';
import DateRangeShortcuts from 'src/common/formFields/DateRangeShortcuts';
import {
  Form, FormikProvider, useFormik
} from 'formik';
import { BASEAPI } from 'src/api/api';
import Loader from 'src/common/Loader';
import { getAllSites } from 'src/redux/siteSlice';
import { filterVehiclesBySite, getAllVehicles, getWailonVehicleList } from 'src/redux/vehicleSlice';
import {
  filterDispenserBySite,
  getAllDispensers
} from 'src/redux/dispenserSlice';
import { getAllVehicleGroupList } from 'src/redux/groupSlice';
import { DATE_FORMAT, REPORT_FILTER_OPTIONS } from 'src/utils/constants';
import { getAllTanks, filterTanksBySite } from 'src/redux/tankSlice';
import AutocompleteField from 'src/common/formFields/AutocompleteField';
import { getAllCards } from 'src/redux/cardSlice';
import useDidUpdate from 'src/hooks/useDidUpdate';
import { getCustomisedMileageList, getOtherReportsData } from 'src/redux/reportSlice';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import getDefaultDateRangeValues from 'src/utils/dateDefaults';
import { getFormattedReportURL, getReportQueryPayload } from './reportService';

const getReportExportFileName = (reportTitle) => {
  if (reportTitle === 'customisedMileageReport') {
    return 'customised-mileage-report';
  }
  if (reportTitle === 'vehicleWiseFuelConsumptionReport') {
    return 'Vehicle-wise Fuel Consumption Report';
  }
  if (reportTitle === 'machineryDieselConsumptionReport') {
    return 'machinery-diesel-consumption-report';
  }

  return reportTitle.toLowerCase();
};

const getBackendErrorMessage = async (error) => {
  const errorResponse = error?.response || error;

  if (errorResponse?.status === 204) {
    return 'No data available! Try with different filters.';
  }

  const responseData = errorResponse?.data;
  if (!responseData) {
    return 'Error downloading the report. Try again later!';
  }

  const resolveMessageFromPayload = (payload) => {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;
    if (Array.isArray(payload)) return payload.find(Boolean) || '';
    return payload.detail
      || payload.message
      || payload.error
      || payload.non_field_errors?.[0]
      || Object.values(payload).flat().find(Boolean)
      || '';
  };

  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      if (!text) return 'Error downloading the report. Try again later!';
      try {
        const parsed = JSON.parse(text);
        return resolveMessageFromPayload(parsed) || text;
      } catch (parseError) {
        return text;
      }
    } catch (blobError) {
      return 'Error downloading the report. Try again later!';
    }
  }

  return resolveMessageFromPayload(responseData) || 'Error downloading the report. Try again later!';
};

function ReportFilters({
  title,
  downloadUrl,
  filterConfig = REPORT_FILTER_OPTIONS,
  formModel,
  datePickerProps,
  formValidation,
  formInitValues,
  tableLimits,
  onViewReport,
  showViewButton = true,
  tableVariant = 'generic',
  showTable = true
}) {
  const dispatch = useDispatch();
  const isViewDisabled = title === 'vehicleGroupWiseReport'
    || title === 'vehicleWiseFuelConsumptionReport';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasViewedReport, setHasViewedReport] = useState(false);
  const submitActionRef = useRef('export');
  const { allSites } = useSelector((state) => state.sites);
  const { vehicleRegNumListBySite, wailonVehicleList } = useSelector((state) => state.vehicles);
  const { dispenserListBySite } = useSelector((state) => state.dispensers);
  const { allVehicleGroups } = useSelector((state) => state.groups);
  const { tankListBySite } = useSelector((state) => state.tanks);
  const { allCards } = useSelector((state) => state.cards);

  const {
    formId,
    formField: {
      site,
      tank,
      month,
      year,
      address,
      assetType,
      requestStatus,
      cardNumber,
      vehicleGroup,
      vehicleNumber,
      wailonVehicleNumber,
      startTime,
      endTime
    }
  } = formModel;

  useEffect(() => {
    setError(false);
    if (filterConfig.site) {
      dispatch(getAllSites());
    }
    if (filterConfig.vehicle) {
      dispatch(getAllVehicles());
    }
    if (filterConfig.wailonVehicle) {
      dispatch(getWailonVehicleList());
    }
    if (filterConfig.dispenser) {
      dispatch(getAllDispensers());
    }
    if (filterConfig.vehicleGroup) {
      dispatch(getAllVehicleGroupList());
    }
    if (filterConfig.tank) {
      dispatch(getAllTanks());
    }
    if (filterConfig.card) {
      dispatch(getAllCards());
    }
  }, [filterConfig]);

  const viewReportList = async (data, pageNo = 1, limit = 10) => {
    const API_URL = downloadUrl.replace('export=true', 'export=false');
    const payload = {
      title, data, startTime, endTime, pageNo, limit
    };
    const apiURL = getFormattedReportURL(API_URL, payload);
    try {
      if (tableVariant === 'customisedMileage') {
        await dispatch(getCustomisedMileageList({
          limit,
          pageNo,
          params: getReportQueryPayload(payload)
        })).unwrap();
      } else {
        await dispatch(getOtherReportsData({ url: apiURL, reportName: title })).unwrap();
      }
    } catch (err) {
      setError('Error getting the report data. Try again later!');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (data) => {
    setLoading(true);
    setError(false);
    const isViewAction = submitActionRef.current === 'view';
    if (isViewAction) {
      onViewReport?.();
      viewReportList(data);
      return;
    }
    const payload = {
      title, data, startTime, endTime
    };
    const apiURL = getFormattedReportURL(downloadUrl, payload, true);
    BASEAPI.get(apiURL, { responseType: 'blob', timeout: 20000 })
      .then((res) => {
        if (res.status !== 200) {
          throw res;
        }
        const fileName = `${getReportExportFileName(title)} ${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`;
        saveAs(res.data, fileName);
      })
      .catch(async (err) => {
        const errMsg = await getBackendErrorMessage(err);
        setError(errMsg);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const initialReportValues = useMemo(() => getDefaultDateRangeValues({
    initialValues: formInitValues,
    startName: startTime?.name,
    endName: endTime?.name,
    onlyDate: datePickerProps?.onlyDate
  }), [datePickerProps?.onlyDate, endTime?.name, formInitValues, startTime?.name]);

  const formikProps = useFormik({
    initialValues: initialReportValues,
    validationSchema: formValidation,
    enableReinitialize: true,
    onSubmit: downloadReport,
  });

  const isFormValidForAutoView = () => {
    try {
      return formValidation?.isValidSync(formikProps.values);
    } catch (err) {
      return false;
    }
  };

  const handleFormSubmit = (jsEvent) => {
    const { name } = jsEvent.nativeEvent.submitter;
    const isView = name === 'view';
    submitActionRef.current = name;
    if (isView) {
      setHasViewedReport(true);
    }
    return formikProps.handleSubmit(jsEvent);
  };

  // filter tank list based on site id on site change
  const onSiteChange = (selectedSiteId) => {
    if (filterConfig.vehicle) {
      dispatch(filterVehiclesBySite(selectedSiteId));
      formikProps.setFieldValue(vehicleNumber.name, 'all');
    }
    if (filterConfig.dispenser) {
      dispatch(filterDispenserBySite(selectedSiteId));
      formikProps.setFieldValue(address.name, 'all');
    }
    if (filterConfig.tank) {
      dispatch(filterTanksBySite(selectedSiteId));
      formikProps.setFieldValue(tank.name, 'all');
    }
  };

  useDidUpdate(() => {
    onSiteChange(formikProps.values[site?.name]);
  }, [formikProps.values[site?.name]]);

  useDidUpdate(() => {
    formikProps.resetForm();
    setHasViewedReport(false);
    if (startTime) { formikProps.setFieldValue(startTime.name, null); }
    if (endTime) { formikProps.setFieldValue(endTime.name, null); }
  }, [downloadUrl]);

  useDidUpdate(() => {
    if (!showTable) {
      return;
    }
    if (hasViewedReport) {
      viewReportList(formikProps.values, tableLimits?.pageNo, tableLimits?.limit);
    } else if (!showViewButton && isFormValidForAutoView()) {
      viewReportList(formikProps.values, tableLimits?.pageNo, tableLimits?.limit);
    }
  }, [showTable, showViewButton, tableLimits]);

  return (
    <FormikProvider value={formikProps}>
      <Form id={formId} onSubmit={handleFormSubmit}>
        {loading ? (
          <Loader />
        ) : (
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {filterConfig.site && (
                <Grid item xs={12} sm={4}>
                  <SelectField
                    {...site}
                    data={[...site.data, ...allSites]}
                    fullWidth
                  />
                </Grid>
              )}
              {filterConfig.dispenser && (
                <Grid item xs={12} sm={4}>
                  <SelectField
                    {...address}
                    data={[...address.data, ...dispenserListBySite]}
                    fullWidth
                  />
                </Grid>
              )}
              {filterConfig.tank && (
                <Grid item xs={12} sm={4}>
                  <SelectField
                    {...tank}
                    data={[...tank.data, ...tankListBySite]}
                    fullWidth
                  />
                </Grid>
              )}
              {filterConfig.card && (
                <Grid item xs={12} sm={4}>
                  <AutocompleteField
                    name={cardNumber.name}
                    label={cardNumber.label}
                    options={[...cardNumber.data, ...allCards]}
                    getOptionLabel={(option) => option.uid}
                    fullWidth
                  />
                </Grid>
              )}
              {filterConfig.wailonVehicle && (
                <Grid item xs={12} sm={4}>
                  <AutocompleteField
                    name={wailonVehicleNumber.name}
                    label={wailonVehicleNumber.label}
                    options={wailonVehicleList}
                    getOptionLabel={(option) => option?.registration_num}
                    fullWidth
                  />
                </Grid>
              )}
              {filterConfig.vehicle && (
                <Grid item xs={12} sm={4}>
                  <AutocompleteField
                    name={vehicleNumber.name}
                    label={vehicleNumber.label}
                    options={[...vehicleNumber.data, ...vehicleRegNumListBySite]}
                    getOptionLabel={(option) => option.registration_num}
                    fullWidth
                  />
                </Grid>
              )}
              {filterConfig.vehicleGroup && (
                <Grid item xs={12} sm={4}>
                  <SelectField
                    {...vehicleGroup}
                    data={[...vehicleGroup.data, ...allVehicleGroups]}
                    fullWidth
                  />
                </Grid>
              )}
              {filterConfig.assetType && (
                <Grid item xs={12} sm={4}>
                  <SelectField
                    {...assetType}
                    data={assetType.data}
                    fullWidth
                  />
                </Grid>
              )}
              {filterConfig.requestStatus && (
                <Grid item xs={12} sm={4}>
                  <SelectField
                    {...requestStatus}
                    fullWidth
                  />
                </Grid>
              )}
              {month && (
                <Grid item xs={12} sm={4}>
                  <SelectField
                    {...month}
                    data={month.data}
                    fullWidth
                    required
                  />
                </Grid>
              )}
              {year && (
                <Grid item xs={12} sm={4}>
                  <SelectField
                    {...year}
                    data={year.data}
                    fullWidth
                    required
                  />
                </Grid>
              )}
              {startTime && (
                <Grid item xs={12} sm={4}>
                  <DatePickerField
                    {...startTime}
                    {...datePickerProps}
                    required
                    disableFuture
                    nativeInput={datePickerProps?.nativeInput ?? true}
                  />
                </Grid>
              )}
              {endTime && (
                <Grid item xs={12} sm={4}>
                  <DatePickerField
                    {...endTime}
                    {...datePickerProps}
                    required
                    disableFuture
                    nativeInput={datePickerProps?.nativeInput ?? true}
                  />
                </Grid>
              )}
              {startTime && endTime && (
                <Grid item xs={12}>
                  <DateRangeShortcuts
                    startName={startTime.name}
                    endName={endTime.name}
                    onlyDate={datePickerProps?.onlyDate}
                    dateFormat={datePickerProps?.dateFormat}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
        )}
        {error && (
        <Typography
          variant="subtitle1"
          sx={{ color: 'red', pt: 2, textAlign: 'center' }}
        >
          {error || ''}
        </Typography>
        )}
        <DialogActions
          sx={{
            m: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            '& > *': {
              width: { xs: '100%', sm: 'auto' },
              m: 0
            }
          }}
        >
          {showViewButton && (
          <Button
            color="primary"
            type="submit"
            name="view"
            disabled={formikProps.isSubmitting || isViewDisabled}
            size="large"
            variant="contained"
          >
            View Report
          </Button>
          )}
          <Button
            color="primary"
            type="submit"
            name="export"
            disabled={formikProps.isSubmitting}
            size="large"
            startIcon={<DownloadIcon />}
            variant="contained"
          >
            Export
          </Button>
        </DialogActions>
      </Form>
    </FormikProvider>
  );
}

ReportFilters.propTypes = {
  filterConfig: PropTypes.object,
  datePickerProps: PropTypes.object,
  formModel: PropTypes.object.isRequired,
  formValidation: PropTypes.object.isRequired,
  formInitValues: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  isViewReport: PropTypes.bool,
  tableLimits: PropTypes.object,
  onViewReport: PropTypes.func,
  downloadUrl: PropTypes.string.isRequired,
  showViewButton: PropTypes.bool,
  tableVariant: PropTypes.string,
  showTable: PropTypes.bool,
};

export default ReportFilters;
